<script>
import { getTerminatedPotentials, getTerminatedPotentialsFilters } from '@/api/cars'
import MultiSelectHint from '@/components/ui/MultiSelectHint.vue'
import { DateTime } from 'luxon'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import EmailColumn from '@/components/ui/datatable/columns/EmailColumn.vue'
import PasswordColumn from '@/components/ui/datatable/columns/PasswordColumn.vue'
import sumBy from 'lodash/sumBy'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import { nl2br } from '@/helpers/utils'
import CarDispatchStatusColumn from '@/components/ui/datatable/columns/CarDispatchStatusColumn.vue'
import ColumnFilter from '@/components/ui/datatable/ColumnFilter.vue'

export default {
  components: {
    'mia-column-filter': ColumnFilter,
    'mia-multi-select-hint': MultiSelectHint
  },

  mixins: [FormattingHelpers],

  data() {
    return {
      dispatchEmployees: [],
      makes: ['CUPRA', 'SEAT', 'SKODA'],
      statusOptions: [
        {
          id: 7,
          label: 'Kundenbestellung auf Lager'
        },
        {
          id: 2,
          label: 'Neubestellung Kunde'
        },
        {
          id: 4,
          label: 'Verkauft-Bestandsfahrzeug'
        }
      ],
      orderStatusOptions: [
        'Hdl Ankunft',
        'Imp Ankunft',
        'Imp Transport',
        'Int Transfer',
        'Kd ausgeliefert'
      ],
      sellers: [],
      terminatedPotentials: [],
      cars: [],
      salesChannels: [],
      filters: {
        dispatchEmployees: [],
        makes: [],
        status: [],
        orderStatus: [],
        sellers: [],
        salesChannels: []
      },
      columns: [
        {
          field: 'id',
          label: 'ID',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:id',
            external: true
          }
        },
        {
          field: 'item_commission_number',
          label: 'Komm.-Nr.',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:id',
            external: true
          }
        },
        {
          field: 'item_type',
          label: 'Typ',
          headerAlign: 'left'
        },
        {
          field: 'item_customer_name',
          label: 'Kunde: Nachname',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/customers/:item_customer',
            external: true
          }
        },
        {
          field: 'item_customer_firstname',
          label: 'Kunde: Vorname',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/customers/:item_customer',
            external: true
          }
        },
        {
          field: 'item_customer_company',
          label: 'Kunde: Firma',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/customers/:item_customer',
            external: true
          }
        },
        {
          field: 'item_dispatch_status',
          label: 'AK Status',
          headerAlign: 'left',
          component: CarDispatchStatusColumn
        },
        {
          field: 'item_dispatch_stage',
          label: 'ALP Status',
          headerAlign: 'left',
          representedAs: row => this.translateDispatchStage(row.item_dispatch_stage)
        },
        {
          field: 'item_invoicedate_sold',
          label: 'RE Datum VK',
          headerAlign: 'left',
          representedAs: row => this.sqlDateToFormattedDate(row.item_invoicedate_sold)
        },
        {
          field: 'item_debts',
          label: 'OPOS Forderungen',
          headerAlign: 'left',
          align: 'right',
          representedAs: row => this.formatAsCurrency(row.item_debts)
        },
        {
          field: 'item_debts_difference',
          label: 'OPOS Differenz',
          headerAlign: 'left',
          align: 'right',
          representedAs: row => this.formatAsCurrency(row.item_debts_difference)
        },
        {
          field: 'item_certificate_slot_title',
          label: 'Brieflagerplatz',
          headerAlign: 'left'
        },
        {
          field: 'item_handover_inspection_date',
          label: 'Geplante Übergabeinspektion',
          headerAlign: 'left',
          representedAs: row => this.sqlDateToFormattedDate(row.item_handover_inspection_date)
        },
        {
          field: 'item_handover_inspection_executed_at',
          label: 'Übergabeinspektion durchgeführt am',
          headerAlign: 'left',
          representedAs: row => this.sqlDateToFormattedDate(row.item_handover_inspection_executed_at)
        },
        {
          field: 'item_preparation_date',
          label: 'Endreinigung',
          headerAlign: 'left',
          representedAs: row => this.sqlDateToFormattedDate(row.item_preparation_date)
        },
        {
          field: 'item_dispatch',
          label: 'Terminierte AK',
          headerAlign: 'left',
          representedAs: row => this.sqlDateToFormattedDateTime(row.item_dispatch)
        },
        {
          field: 'item_dispatch_notes',
          label: 'Auslieferungsnotizen',
          headerAlign: 'left',
          interpolate: true,
          representedAs: row => `<p style="margin: 0; padding: 0;">${this.nl2br(row.item_dispatch_notes)}</p>`
        },
        {
          field: 'item_productiondata_order_status',
          label: 'NADIN Status',
          headerAlign: 'left'
        },
        {
          field: 'item_status_option_title',
          label: 'CARANA Status',
          headerAlign: 'left',
          interpolate: true,
          representedAs: row => `<span style="color: #${row.item_status_option_color}">${row.item_status_option_title}</span>`
        },
        {
          field: 'item_dispatch_skoda_connect_email',
          label: 'ŠKODA Connect E-Mail',
          headerAlign: 'left',
          component: EmailColumn
        },
        {
          field: 'item_dispatch_skoda_connect_password',
          label: 'ŠKODA Connect Passwort',
          headerAlign: 'left',
          component: PasswordColumn
        }
      ],
      columnConfig: [
        {
          column: 'id',
          label: 'ID',
          isToggleable: false,
          isDefault: true
        },
        {
          column: 'item_commission_number',
          label: 'Komm.-Nr.',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_type',
          label: 'Typ',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_customer_name',
          label: 'Kunde: Nachname',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_customer_firstname',
          label: 'Kunde: Vorname',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_customer_company',
          label: 'Kunde: Firma',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_dispatch_status',
          label: 'AK Status',
          isToggleable: false,
          isDefault: true
        },
        {
          column: 'item_dispatch_stage',
          label: 'ALP Status',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_invoicedate_sold',
          label: 'RE Datum VK',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_debts',
          label: 'OPOS Forderungen',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_debts_difference',
          label: 'OPOS Differenz',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_certificate_slot_title',
          label: 'Brieflagerplatz',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_handover_inspection_date',
          label: 'Geplante Übergabeinspektion',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_handover_inspection_executed_at',
          label: 'Übergabeinspektion durchgeführt am',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_preparation_date',
          label: 'Endreinigung',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_dispatch',
          label: 'Terminierte AK',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_dispatch_notes',
          label: 'Auslieferungsnotizen',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_productiondata_order_status',
          label: 'NADIN Status',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_status_option_title',
          label: 'CARANA Status',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_dispatch_skoda_connect_email',
          label: 'ŠKODA Connect E-Mail',
          isToggleable: true,
          isDefault: true
        },
        {
          column: 'item_dispatch_skoda_connect_password',
          label: 'ŠKODA Connect Passwort',
          isToggleable: true,
          isDefault: true
        }
      ],
      visibleColumns: []
    }
  },

  computed: {
    sumDebts() {
      return sumBy(this.cars.map(car => ({ ...car, item_debts: parseFloat(car.item_debts) })), 'item_debts')
    },

    sumDebtsFormatted() {
      return this.formatAsCurrency(this.sumDebts)
    },

    activeColumns() {
      return this.columns.filter(({ field }) => this.visibleColumns.includes(field))
    }
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.loadTerminatedPotentials()
      }
    },

    visibleColumns(newVisibleColumns) {
      window.localStorage.setItem('carana_terminated_potentials_filtered_columns', JSON.stringify(newVisibleColumns))
    }
  },

  created() {
    this.loadTerminatedPotentialsFilters()
    this.loadTerminatedPotentials()
    this.$store.dispatch('loadDispatchStatus')
    this.restoreVisibleColumns()
  },

  methods: {
    nl2br,

    async loadTerminatedPotentialsFilters() {
      this.$wait.start('loading terminated potentials filters')
      const [error, response] = await getTerminatedPotentialsFilters()
      this.$wait.end('loading terminated potentials filters')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Die Filter konnten nicht geladen werden']
        })
        return
      }

      this.dispatchEmployees = response.dispatchEmployees
      this.sellers = response.sellers
      this.salesChannels = response.salesChannels
    },

    async loadTerminatedPotentials() {
      this.$wait.start('loading terminated potentials')
      const [error, response] = await getTerminatedPotentials({
        dispatch_employees: this.filters.dispatchEmployees,
        makes: this.filters.makes,
        status: this.filters.status,
        order_status: this.filters.orderStatus,
        sellers: this.filters.sellers,
        sales_channels: this.filters.salesChannels
      })
      this.$wait.end('loading terminated potentials')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Die terminierten Potentiale konnten nicht geladen werden']
        })
        return
      }

      this.cars = response
    },

    translateDispatchStage(stage) {
      return {
        '001_started': '100: Gestartet',
        '100_started': '100: Gestartet',
        '200_package_selection': '200: Paketauswahl',
        '300_waiting_for_payment': '300: Warten auf Zahlungseingang',
        '400_registration': '400: Zulassung',
        '500_appointment_selection': '500: Terminvereinbarung',
        '1000_completed': '1000: Abgeschlossen'
      }[stage] || stage
    },

    sqlDateToFormattedDate(date) {
      if (!date) {
        return ''
      }

      return DateTime.fromSQL(date).toFormat('dd.MM.yyyy')
    },

    sqlDateToFormattedDateTime(date) {
      if (!date || date === '0000-00-00 00:00:00') {
        return ''
      }

      return `${DateTime.fromSQL(date).toFormat('dd.MM.yyyy, HH:mm')} Uhr`
    },

    restoreVisibleColumns() {
      const storedColumns = window.localStorage.getItem('carana_terminated_potentials_filtered_columns')

      if (storedColumns) {
        try {
          this.visibleColumns = JSON.parse(storedColumns)
        } catch (e) {
          this.visibleColumns = []
        }
      } else {
        this.visibleColumns = []
      }

      if (this.visibleColumns.length === 0) {
        this.resetVisibleColumns()
      }
    },

    resetVisibleColumns() {
      this.visibleColumns = this.columnConfig
        .filter(({ isDefault }) => isDefault)
        .map(({ column }) => column)
    }
  }
}
</script>

<template>
  <div>
    <mia-wait
      for="loading terminated potentials filters"
      class="mt-4"
    >
      <template #waiting>
        <div class="flex items-center justify-center py-8">
          <mia-wave-loader />
        </div>
      </template>

      <template #default>
        <div class="grid grid-cols-6 gap-3 p-4 -mx-2 bg-gray-200 items-start">
          <div
            class="relative flex flex-col justify-end"
          >
            <label
              for="dispatchEmployeeSelect"
              class="mb-1 mr-2 tracking-wider text-gray-800"
            >
              Auslieferer
            </label>

            <select
              id="dispatchEmployeeSelect"
              v-model="filters.dispatchEmployees"
              class="form-select py-2 pl-3 bg-white bg-no-repeat border border-gray-500 rounded appearance-none"
              multiple
            >
              <option
                v-for="dispatchEmployee in dispatchEmployees"
                :key="dispatchEmployee.id"
                class="text-black"
                :value="dispatchEmployee.id"
              >
                {{ `${dispatchEmployee.item_lastname}, ${dispatchEmployee.item_firstname}` }}
              </option>
            </select>
            <mia-multi-select-hint class="mt-1" />
          </div>
          <div
            class="relative flex flex-col justify-end"
          >
            <label
              for="makeSelect"
              class="mb-1 mr-2 tracking-wider text-gray-800"
            >
              Hersteller
            </label>

            <select
              id="makeSelect"
              v-model="filters.makes"
              class="form-select py-2 pl-3 bg-white bg-no-repeat border border-gray-500 rounded appearance-none"
              multiple
            >
              <option
                v-for="make in makes"
                :key="make"
                class="text-black"
                :value="make"
              >
                {{ make }}
              </option>
            </select>
            <mia-multi-select-hint class="mt-1" />
          </div>
          <div
            class="relative flex flex-col justify-end"
          >
            <label
              for="statusSelect"
              class="mb-1 mr-2 tracking-wider text-gray-800"
            >
              Status
            </label>

            <select
              id="statusSelect"
              v-model="filters.status"
              class="form-select py-2 pl-3 bg-white bg-no-repeat border border-gray-500 rounded appearance-none"
              multiple
            >
              <option
                v-for="statusOption in statusOptions"
                :key="statusOption.id"
                class="text-black"
                :value="statusOption.id"
              >
                {{ statusOption.label }}
              </option>
            </select>
            <mia-multi-select-hint class="mt-1" />
          </div>
          <div
            class="relative flex flex-col justify-end"
          >
            <label
              for="orderStatusSelect"
              class="mb-1 mr-2 tracking-wider text-gray-800"
            >
              Produktionsstatus
            </label>

            <select
              id="orderStatusSelect"
              v-model="filters.orderStatus"
              class="form-select py-2 pl-3 bg-white bg-no-repeat border border-gray-500 rounded appearance-none"
              multiple
            >
              <option
                v-for="orderStatusOption in orderStatusOptions"
                :key="orderStatusOption"
                class="text-black"
                :value="orderStatusOption"
              >
                {{ orderStatusOption }}
              </option>
            </select>
            <mia-multi-select-hint class="mt-1" />
          </div>
          <div
            class="relative flex flex-col justify-end"
          >
            <label
              for="sellerSelect"
              class="mb-1 mr-2 tracking-wider text-gray-800"
            >
              Verkäufer
            </label>

            <select
              id="sellerSelect"
              v-model="filters.sellers"
              class="form-select py-2 pl-3 bg-white bg-no-repeat border border-gray-500 rounded appearance-none"
              multiple
            >
              <option
                v-for="seller in sellers"
                :key="seller.id"
                class="text-black"
                :value="seller.id"
              >
                {{ seller.item_lastname }}, {{ seller.item_firstname }}
              </option>
            </select>
            <mia-multi-select-hint class="mt-1" />
          </div>
          <div
            class="relative flex flex-col justify-end"
          >
            <label
              for="salesChannelsSelect"
              class="mb-1 mr-2 tracking-wider text-gray-800"
            >
              Vertriebskanal
            </label>

            <select
              id="salesChannelsSelect"
              v-model="filters.salesChannels"
              class="form-select py-2 pl-3 bg-white bg-no-repeat border border-gray-500 rounded appearance-none"
              multiple
            >
              <option
                v-for="salesChannel in salesChannels"
                :key="salesChannel.id"
                class="text-black"
                :value="salesChannel.id"
              >
                {{ salesChannel.item_title }}
              </option>
            </select>
            <mia-multi-select-hint class="mt-1" />
          </div>
        </div>
      </template>
    </mia-wait>

    <mia-wait
      for="loading terminated potentials"
      class="mt-6"
    >
      <template #waiting>
        <div class="flex items-center justify-center py-8">
          <mia-wave-loader />
        </div>
      </template>

      <template #default>
        <div class="mt-6">
          <div class="p-4 bg-gray-200 rounded-t">
            <mia-column-filter
              v-model="visibleColumns"
              :config="columnConfig"
              @reset="resetVisibleColumns"
            />
          </div>
          <div class="w-full overflow-x-auto">
            <datatable
              ref="datatableRef"
              :columns="activeColumns"
              :data="cars"
              class="sticky w-full table-auto datatable"
            >
              <template #no-results>
                <div class="flex justify-center">
                  Keine Einträge gefunden.
                </div>
              </template>
              <template
                v-if="cars.length > 0"
                #footer
              >
                <tr>
                  <td
                    class="font-bold"
                    colspan="7"
                  >
                    Gesamt
                  </td>
                  <td class="font-bold text-right">
                    {{ sumDebtsFormatted }}
                  </td>
                  <td colspan="10" />
                </tr>
              </template>
            </datatable>
          </div>
        </div>
      </template>
    </mia-wait>
  </div>
</template>
